import { CustomSegmentType } from '../types';
import { SavedCustomFeatureSegments, SavedCustomPriceClass } from '../../types/content/custom.types';
import { Formatting } from '../../data/formatting';
import { ColumnSection, Section, SectionType, SelectionMenu } from '../../types';

export type SavedCustomSegmentsType = SavedCustomPriceClass | SavedCustomFeatureSegments;

interface ErrorMessage {
  nameExisted: string;
}

interface DeleteSavedModalLabelText {
  header: string;
  infoText: string;
  subText: string;
}
interface SavedCardLabels {
  applyButtonLabels: string;
  deleteButtonLabels: string;
  shareButtonLabels: string;
  testId: string;
}

interface SaveModalLabelText {
  header: string;
  placeholder: string;
}

interface CustomSegmentsStaticLabels {
  savedModalLabelText: SaveModalLabelText;
  deleteSavedModalLabelText: DeleteSavedModalLabelText;
  savedCardLabels: SavedCardLabels;
  errorMessage: ErrorMessage;
}

export const isSavedCustomPriceClass = (
  savedCustomData: SavedCustomSegmentsType
): savedCustomData is SavedCustomPriceClass => {
  return 'priceClasses' in savedCustomData;
};

export const getCustomSegmentsLabelDetails = (
  savedCustomData?: SavedCustomSegmentsType
): Record<CustomSegmentType, CustomSegmentsStaticLabels> => {
  const currencySymbol =
    savedCustomData && isSavedCustomPriceClass(savedCustomData)
      ? Formatting.getCurrencySymbol(savedCustomData?.priceClasses[0].currency)
      : '';

  return {
    [CustomSegmentType.customPriceClass]: {
      savedModalLabelText: {
        header: 'Save price classes',
        placeholder: 'Name of new price classes',
      },
      deleteSavedModalLabelText: {
        header: 'Delete price classes',
        infoText: `Are you sure you want to delete the price classes "${savedCustomData?.name}" (${currencySymbol})?`,
        subText: 'Analysis having this price class definition applied are not affected',
      },
      savedCardLabels: {
        applyButtonLabels: 'Apply Saved Custom Price Classes',
        shareButtonLabels: 'Share Saved Custom Price Classes',
        deleteButtonLabels: 'Delete Saved Custom Price Classes',
        testId: 'saved-custom-card',
      },
      errorMessage: {
        nameExisted: 'Price Classes name must be unique',
      },
    },
    [CustomSegmentType.customFeatureSegmentation]: {
      savedModalLabelText: {
        header: 'Save feature segment',
        placeholder: 'Name of new feature segments',
      },
      deleteSavedModalLabelText: {
        header: 'Delete feature segment',
        infoText: `Are you sure you want to delete the feature segment "${savedCustomData?.name}"?`,
        subText: 'Analysis having this feature segment definition applied are not affected',
      },
      savedCardLabels: {
        applyButtonLabels: 'Apply Saved Custom Feature Segments',
        shareButtonLabels: 'Share Saved Custom Segmentation',
        deleteButtonLabels: 'Delete Saved Custom Segmentation',
        testId: 'saved-custom-segmentation-card',
      },
      errorMessage: {
        nameExisted: 'New feature segments name must be unique',
      },
    },
  };
};

/**
 * Retrieves the current tab name or param name from the dsl sections for custom segments
 * @param sections the Sections array from the dsl
 * @param returnType the type of value to return ('name' or 'paramName')
 * @returns the name or param name of the current tab
 */
export function getCurrentActiveComponentNameForCustomTray(sections: Section[]): string {
  for (const section of sections) {
    if (section.type !== SectionType.column) continue;

    for (const col of (section as ColumnSection).columns) {
      const { heading } = col.component;
      if (typeof heading.title === 'string') continue;
      const menuWithCustomizationTray = heading.title.find(
        titleItem => typeof titleItem !== 'string' && !!(titleItem.menu as SelectionMenu).customizationTraySettings
      );
      if (menuWithCustomizationTray) {
        return col.component.name;
      }
    }
  }
  return '';
}

/**
 * Compare the list by preferenceId to determine if the saved features list is different
 * @param newSavedFeatureSegments - the new list to be updated to
 * @param currentSavedFeatures - the current list in the component state
 * @returns - whether the saved features list is different
 */
export const isDiffSavedFeatures = (
  newSavedFeatureSegments: SavedCustomFeatureSegments[],
  currentSavedFeatures: SavedCustomFeatureSegments[]
): boolean => {
  if (newSavedFeatureSegments.length !== currentSavedFeatures.length) {
    return true;
  }
  return newSavedFeatureSegments.some((item, index) => item.preferenceId !== currentSavedFeatures[index].preferenceId);
};
